import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faUpload } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { generateRedirectUrl, digObject } from '@domain-group/javascript-utils'

import { generateUrls as generateArtworkUrls } from '@hooks/useArtwork'
import { generateUrls as generateCampaignUrls } from '@hooks/useCampaign'
import useOrderItem, { useOrderItemForm } from '@hooks/useOrderItem'
import useService from '@hooks/useService'
import useStatuses from '@hooks/useStatuses'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import ArtworkUploader from './components/ArtworkUploader'

const MODAL_KEY = 'ManageProviderOrderItemModal'

export type ModalCallbacks = {
  closeModal: () => void,
  updateProviderOrderItem: (payload: HandleCallbackActionParams) => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageProviderOrderItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateProviderOrderItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { orderItem: initOrderItem } = modalPayload

  const orderItemPayload = useOrderItem(initOrderItem)
  const {
    artwork,
    callbacks: {
      updateOrderItem: updateFn,
    },
    order,
    order: {
      // @ts-expect-error TS2339
      campaign_id,
      // @ts-expect-error TS2339
      order_type,
    },
    orderItem,
    service,
    updating,
  } = orderItemPayload

  const { artworkDownloadPdfUrl } = generateArtworkUrls(artwork)

  const orderItemFormPayload = useOrderItemForm(orderItem, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      status_id,
    },
    handlers,
  } = orderItemFormPayload

  const updateOrderItemPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Order Item Updated',
  }

  const { isImageable } = useService(service)

  const { filteredStatuses } = useStatuses()

  const statuses = filteredStatuses
    // @ts-expect-error TS2339
    .filter(status => digObject(status, 'options.services', []).includes(service.short_name))

  const { campaignMediaProviderUrl } = generateCampaignUrls({ id: campaign_id })
  const payload = encodeURIComponent(JSON.stringify({ orderItem: { id: orderItem?.id } }))

  const campaignMediaProviderUrlWithRedirect = `${campaignMediaProviderUrl}?redirect=${generateRedirectUrl({
    append: `showModal=${MODAL_KEY}&showModalPayload=${payload}`,
  })}`

  const showArtworkUpload = order_type === 'custom' && !!campaign_id
  const showUploadMedia = !!campaign_id && isImageable

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Order Item" titleSecondLine="Details" />

      <SidebarModal.Content>
        <Form>
          {showArtworkUpload && (
            // @ts-expect-error TS2740
            <ArtworkUploader order={order} order_item={orderItem} />
          )}

          {showUploadMedia && (
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryUtility"
              href={campaignMediaProviderUrlWithRedirect}
              icon={<FontAwesomeIcon icon={faUpload} />}
              marginTop={showArtworkUpload ? 'large' : undefined}
              size="medium"
            >
              Upload Media
            </Button>
          )}

          <Form.Field label="Status" marginTop={showUploadMedia || showArtworkUpload ? 'large' : undefined}>
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="status_id"
              value={status_id}
              {...handlers}
            >
              {statuses.map(status => <option value={status.id}>{status.title}</option>)}
            </select>
          </Form.Field>

          {/*
           // @ts-expect-error TS2339 */}
          {artwork?.id && (
            <Form.Field label="More Options" marginTop="large">
              <AuthenticatedDownload>
                <Button
                  // @ts-expect-error TS2322
                  as="a"
                  buttonStyle="secondaryUtility"
                  href={artworkDownloadPdfUrl}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  size="medium"
                >
                  Download PDF
                </Button>
              </AuthenticatedDownload>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateProviderOrderItem(updateOrderItemPayload)}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageProviderOrderItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
